import { useCallback, useEffect, useRef } from 'react';

import type { UUID } from '@aline/common';
import { getMimeTypeDisplayLabel } from '@aline/common';
import {
  getGridNumericOperators,
  getGridStringOperators,
  GRID_CHECKBOX_SELECTION_FIELD,
  gridColumnVisibilityModelSelector,
  gridFilterModelSelector,
  GridLogicOperator,
  useGridApiContext,
} from '@mui/x-data-grid-premium';
import type {
  GridApi,
  GridApiPremium,
  GridColDef,
  GridColumnsInitialState,
  GridColumnVisibilityModel,
  GridFilterItem,
  GridInitialState,
} from '@mui/x-data-grid-premium';
import * as Sentry from '@sentry/react';
import cleanDeep from 'clean-deep';
import { isValid, parseISO } from 'date-fns';
import { isEqual } from 'lodash';
import { isEmpty, omit, pick } from 'ramda';
import type { ReportGridColDef } from 'src/components';
import {
  allBooleanFilters,
  allContractTypeFilters,
  allFileTypeFilters,
  allLabelFilters,
  allOwnerFilters,
  allPartyFilters,
  allQuestionSelectFilters,
  booleanIsFilterOperator,
  ColumnHeader,
  dateFilterOperators,
  documentStatusFilterOperator,
  getExtractionCellValue,
  getIsOwnerCellValue,
  ReportCell,
} from 'src/components';
import { Apollo } from 'src/gql/generated';
import { ReportColumnFileField } from 'src/gql/generated/apollo-types';
import { getDocStatusLabelProps, getIsDocumentStatus } from 'src/helpers';
import { useDebouncedCallback } from 'use-debounce';
import { v4 as uuid4 } from 'uuid';

export type ReportGridState = GridInitialState;

export type GridReportColOption =
  | Apollo.FileFieldColumn
  | Omit<Apollo.ExtractionReportColumn, 'question'>;

export const REPORT_ROW_STATUS_FIELD = 'rowStatus' satisfies ReportGridColDef['field'];
export const HIDDEN_REPORT_RUNTIME_COLUMN_FIELDS = [REPORT_ROW_STATUS_FIELD];

// Note: Could not find this strongly typed anywhere in MUI so had to hard-code the string.
const isAnyOfString = 'isAnyOf';
const stringOpsWithoutIsAnyOf = getGridStringOperators().filter(op => {
  // Remove isAnyOf operator (not supported for string columns in reports)
  return op.value !== isAnyOfString;
});
const numOpsWithoutIsAnyOf = getGridNumericOperators().filter(op => op.value !== isAnyOfString);

export interface GridReport extends Pick<Apollo.Report, 'id' | 'name' | 'state' | '__typename'> {
  columnOptions: GridReportColOption[];
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function dateValueGetter(value: any): Date | null {
  if (!value) return null;
  if (value instanceof Date) return isValid(value) ? value : null;
  try {
    const date = parseISO(value);
    return isValid(date) ? date : null;
  } catch {
    console.warn('Invalid date value:', value);
    return null;
  }
}

/**
 * Given some column definitions, group them by column type.
 */
export function groupByColumnType(columnDefs: ReportGridColDef[]): {
  extractionColumns: ReportGridColDef[];
  systemColumns: ReportGridColDef[];
} {
  return columnDefs.reduce<{
    extractionColumns: ReportGridColDef[];
    systemColumns: ReportGridColDef[];
  }>(
    (acc, column) => {
      switch (column.__typename) {
        case 'FileFieldColumn':
          acc.systemColumns.push(column);
          return acc;
        case 'ExtractionReportColumn':
          acc.extractionColumns.push(column);
          return acc;
      }
    },
    { extractionColumns: [], systemColumns: [] }
  );
}

// Create column definition for report.
export function createReportGridColDef(
  colOption: GridReportColOption,
  overrides: Partial<ReportGridColDef> = {}
): ReportGridColDef {
  const { displayName } = colOption;
  const field = getColOptionIdentifier(colOption);
  const colWidthProps = getColWidthProperties(colOption);
  const columnSpecificOpts = getColumnSpecificOpts(colOption);

  return {
    __typename: colOption.__typename,
    field,
    // Feeds the Display Options grouping picker — see `useGroupableColumns`.
    groupable: colOption.isGroupable,
    headerAlign: 'left',
    headerName: displayName || 'Untitled',
    renderCell: ReportCell,
    renderHeader: ColumnHeader,
    ...colWidthProps,
    ...columnSpecificOpts,
    ...overrides,
  };
}

export function createBuiltInReportFilterColDef(field: Apollo.ReportColumnFileField): GridColDef {
  const baseColumn = {
    field: getFileFieldIdentifier(field),
    filterable: true,
    headerName: getBuiltInReportColumnHeader(field),
  };

  switch (field) {
    case ReportColumnFileField.Owner:
      return { ...baseColumn, filterOperators: allOwnerFilters };
    case ReportColumnFileField.Parties:
      return { ...baseColumn, filterOperators: allPartyFilters };
    case ReportColumnFileField.Status:
      return { ...baseColumn, filterOperators: [documentStatusFilterOperator] };
    case ReportColumnFileField.Type:
      return { ...baseColumn, filterOperators: allContractTypeFilters };
    case ReportColumnFileField.Labels:
      return { ...baseColumn, filterOperators: allLabelFilters };
    case ReportColumnFileField.FileType:
      return { ...baseColumn, filterOperators: allFileTypeFilters, type: 'string' };
    case ReportColumnFileField.RequesterFavorited:
      return { ...baseColumn, filterOperators: [booleanIsFilterOperator], type: 'boolean' };
    case ReportColumnFileField.CreatedAt:
    case ReportColumnFileField.UpdatedAt:
    case ReportColumnFileField.DeletedAt:
    case ReportColumnFileField.SignedAt:
    case ReportColumnFileField.LastAccessedByRequesterAt:
      return { ...baseColumn, filterOperators: dateFilterOperators, type: 'dateTime' };
    case ReportColumnFileField.Id:
    case ReportColumnFileField.Name:
      return { ...baseColumn, filterOperators: stringOpsWithoutIsAnyOf, type: 'string' };
    default:
      return { ...baseColumn, filterOperators: stringOpsWithoutIsAnyOf, type: 'string' };
  }
}

// Get width properties for a given column.
export function getColWidthProperties(
  colOption: GridReportColOption
): Pick<ReportGridColDef, 'flex' | 'minWidth'> {
  if (colOption.__typename === 'ExtractionReportColumn') return { flex: 1, minWidth: 250 };

  switch (colOption.field) {
    case ReportColumnFileField.Labels:
    case ReportColumnFileField.Parties:
    case ReportColumnFileField.Type:
      return { flex: 0, minWidth: 200 };
    case ReportColumnFileField.FileType:
    case ReportColumnFileField.Status:
      return { flex: 0, minWidth: 120 };
    case ReportColumnFileField.Owner:
    case ReportColumnFileField.RequesterFavorited:
      return { flex: 0, minWidth: 0 };
    case ReportColumnFileField.CreatedAt:
    case ReportColumnFileField.UpdatedAt:
    case ReportColumnFileField.DeletedAt:
      return { flex: 0, minWidth: 140 };
    default:
      return { flex: 1, minWidth: 250 };
  }
}

export function getIsPartyCellValue(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: any
): value is Partial<Pick<Apollo.Party, 'name' | '__typename'>>[] {
  return Array.isArray(value) && value.every(val => typeof val === 'object');
}

export function getColumnSpecificOpts(
  colOption: GridReportColOption
): Pick<
  GridColDef,
  | 'filterOperators'
  | 'getApplyQuickFilterFn'
  | 'sortable'
  | 'sortComparator'
  | 'type'
  | 'valueFormatter'
  | 'valueGetter'
> {
  switch (colOption.__typename) {
    case 'FileFieldColumn': {
      switch (colOption.field) {
        case ReportColumnFileField.Owner:
          return {
            filterOperators: allOwnerFilters,
            getApplyQuickFilterFn: (value: string) => {
              return ({ value: cellValue }) => {
                return (
                  getIsOwnerCellValue(cellValue) &&
                  typeof cellValue.displayName === 'string' &&
                  cellValue.displayName.toLowerCase().includes(value.toLowerCase())
                );
              };
            },
            sortComparator: (v1, v2) => {
              const v1Name = getIsOwnerCellValue(v1) ? (v1.displayName ?? '') : '';
              const v2Name = getIsOwnerCellValue(v2) ? (v2.displayName ?? '') : '';
              return v1Name.localeCompare(v2Name);
            },
            type: colOption.dataType,
          };
        case ReportColumnFileField.Parties:
          return {
            filterOperators: allPartyFilters,
            getApplyQuickFilterFn: (value: string) => {
              return ({ value: cellValue }) => {
                return (
                  getIsPartyCellValue(cellValue) &&
                  cellValue.some(
                    party =>
                      typeof party.name === 'string' &&
                      party.name.toLowerCase().includes(value.toLowerCase())
                  )
                );
              };
            },
            sortable: true,
            type: colOption.dataType,
          };
        case ReportColumnFileField.Status:
          return {
            filterOperators: [documentStatusFilterOperator],
            getApplyQuickFilterFn: (value: string) => {
              return cellParams => {
                const { value: status } = cellParams;
                if (!getIsDocumentStatus(status)) return false;

                const { label: displayStatus } = getDocStatusLabelProps(status);
                return displayStatus.toLowerCase().includes(value.toLowerCase());
              };
            },
            type: colOption.dataType,
          };
        case ReportColumnFileField.Type:
          return {
            filterOperators: allContractTypeFilters,
            getApplyQuickFilterFn: (value: string) => {
              return cellParams => {
                const { value: contractType } = cellParams;
                return (
                  getIsTypeCellValue(contractType) &&
                  typeof contractType.value === 'string' &&
                  contractType.value.toLowerCase().includes(value.toLowerCase())
                );
              };
            },
            type: colOption.dataType,
          };
        case ReportColumnFileField.Labels:
          return {
            filterOperators: allLabelFilters,
            sortable: true,
          };
        case ReportColumnFileField.FileType:
          return {
            filterOperators: allFileTypeFilters,
            getApplyQuickFilterFn: (value: string) => {
              return cellParams => {
                const { value: fileType } = cellParams;
                return (
                  typeof fileType === 'string' &&
                  fileType.toLowerCase().includes(value.toLowerCase())
                );
              };
            },
            type: 'string',
            valueGetter: (_value, row) => {
              const mimetype = row?.latestVersion?.fileLocation?.mimetype ?? null;
              return getMimeTypeDisplayLabel(mimetype);
            },
          };
        default:
          switch (colOption.dataType) {
            case 'string':
              return {
                filterOperators: stringOpsWithoutIsAnyOf,
                type: colOption.dataType,
              };
            case 'number':
              return {
                filterOperators: numOpsWithoutIsAnyOf,
                type: colOption.dataType,
              };
            case 'date':
            case 'dateTime':
              return {
                filterOperators: dateFilterOperators,
                type: colOption.dataType,
                valueGetter: dateValueGetter,
              };
            case 'boolean':
              return {
                filterOperators: [booleanIsFilterOperator],
                type: colOption.dataType,
              };
            default:
              return { type: colOption.dataType };
          }
      }
    }
    case 'ExtractionReportColumn': {
      switch (colOption.dataType) {
        case Apollo.ReportColumnDataType.String:
          return {
            filterOperators: stringOpsWithoutIsAnyOf,
            type: colOption.dataType,
            valueGetter: value => getExtractionCellValue(value, colOption.dataType),
          };
        case Apollo.ReportColumnDataType.Number:
          return {
            filterOperators: numOpsWithoutIsAnyOf,
            type: colOption.dataType,
            valueGetter: value => getExtractionCellValue(value, colOption.dataType),
          };
        case Apollo.ReportColumnDataType.Date:
        case Apollo.ReportColumnDataType.DateTime: {
          return {
            filterOperators: dateFilterOperators,
            type: colOption.dataType,
            valueGetter: value => getExtractionCellValue(value, colOption.dataType),
          };
        }
        case Apollo.ReportColumnDataType.SingleSelect:
          return {
            filterOperators: allQuestionSelectFilters,
            type: colOption.dataType,
            valueGetter: value => getExtractionCellValue(value, colOption.dataType),
          };
        case Apollo.ReportColumnDataType.Boolean:
          return {
            filterOperators: allBooleanFilters,
            type: colOption.dataType,
            valueFormatter: value => (value === true ? 'Yes' : value === false ? 'No' : '-'),
            valueGetter: value => getExtractionCellValue(value, colOption.dataType),
          };
        default: {
          return {
            type: colOption.dataType,
            valueGetter: value => getExtractionCellValue(value, colOption.dataType),
          };
        }
      }
    }
  }
}

export function getColOptionIdentifier(colOption: GridReportColOption): string {
  switch (colOption.__typename) {
    case 'FileFieldColumn':
      return getFileFieldIdentifier(colOption.field);
    case 'ExtractionReportColumn':
      return colOption.questionId;
  }
}

/**
 * Returns the field identifier for a given ReportColumnFileField.
 * Most fields map directly to Document properties, but some (like fileType)
 * are computed columns that don't exist on the Document model.
 */
export function getFileFieldIdentifier(
  field: Apollo.ReportColumnFileField
): keyof Apollo.Document | 'fileType' {
  return field;
}

/**
 * Get a bunch of funcs for manipulating filters.
 */
export function useFilterFuncs(): {
  applyFilterLogicOperator: (operator: GridLogicOperator) => void;
  createFilter: () => void;
  deleteFilter: (item: GridFilterItem) => void;
  upsertFilter: (item: GridFilterItem) => void;
} {
  const apiRef = useGridApiContext();

  const applyFilterLogicOperator = useCallback(
    (operator: GridLogicOperator) => {
      const filterModel = gridFilterModelSelector(apiRef);
      apiRef.current.setFilterModel({
        ...filterModel,
        logicOperator: operator,
      });
    },
    [apiRef]
  );

  const createFilter = useCallback(() => {
    const filterModel = gridFilterModelSelector(apiRef);
    const columns = apiRef.current.getAllColumns();
    const filterableColumns = columns.filter(col => col.filterable !== false);
    const firstFilterableColumn = filterableColumns.at(0);

    if (!firstFilterableColumn) return;

    // Create a new filter item with a unique ID
    const newItem: GridFilterItem = {
      // Generate unique ID
      field: firstFilterableColumn.field,
      id: uuid4(),
      operator: firstFilterableColumn.filterOperators?.at(0)?.value || 'contains',
      value: '',
    };

    // Update the filter model with the new item
    apiRef.current.setFilterModel({
      ...filterModel,
      items: [...filterModel.items, newItem],
    });
  }, [apiRef]);

  const deleteFilter = useCallback(
    (item: GridFilterItem) => {
      const filterModel = gridFilterModelSelector(apiRef);
      const newItems = filterModel.items.filter(i => i.id !== item.id);

      // If we're removing the last filter, reset the model
      if (newItems.length === 0) {
        apiRef.current.setFilterModel({
          items: [],
          logicOperator: GridLogicOperator.And,
        });
      } else {
        apiRef.current.setFilterModel({
          ...filterModel,
          items: newItems,
        });
      }
    },
    [apiRef]
  );

  const upsertFilter = useCallback(
    (item: GridFilterItem) => {
      const filterModel = gridFilterModelSelector(apiRef);
      const itemIndex = filterModel.items.findIndex(i => i.id === item.id);
      const newItems = [...filterModel.items];
      if (itemIndex === -1) {
        newItems.push(item);
      } else {
        newItems[itemIndex] = item;
      }

      // Update the filter model
      apiRef.current.setFilterModel({
        ...filterModel,
        items: newItems,
      });
    },
    [apiRef]
  );

  return {
    applyFilterLogicOperator,
    createFilter,
    deleteFilter,
    upsertFilter,
  };
}

/**
 * Positions a column before the actions column (which is always the last column).
 * This ensures new columns are added at the end minus 1 position.
 */
export function getColumnPositionBeforeActions(
  apiRef: React.RefObject<GridApiPremium | null>
): number {
  if (!apiRef.current) return 0;
  const visibleColumns = apiRef.current.getVisibleColumns();
  const actionsColumnIndex = visibleColumns.findIndex(col => col.field === 'actions');
  // If actions column exists, place before it. Otherwise, place at the end.
  // Use Math.max to ensure we don't return a negative index
  return actionsColumnIndex !== -1
    ? Math.max(0, actionsColumnIndex)
    : Math.max(0, visibleColumns.length);
}

/**
 * Shows a column smoothly at the correct position without visual jumping.
 */
export function showColumnAtPosition(
  apiRef: React.RefObject<GridApiPremium | null>,
  field: string,
  targetIndex: number
): void {
  if (!apiRef.current) return;
  apiRef.current.setColumnVisibilityModel({
    ...gridColumnVisibilityModelSelector(apiRef),
    [field]: true,
  });
  apiRef.current.setColumnIndex(field, targetIndex);
  apiRef.current.setColumnVisibility(field, true);
}

// Type guard to check if incoming state is GridInitialState.
export function getIsGridState(state: Record<string, unknown>): state is Partial<GridInitialState> {
  return typeof state === 'object';
}

export function getSavedRowGroupingModel(state: Record<string, unknown>): string[] | undefined {
  if (!getIsGridState(state)) return undefined;
  return state.rowGrouping?.model;
}

type GridColumnsState = NonNullable<GridInitialState['columns']>;

// Returns GridInitialState.columns but *only the visible column data*.
export function getVisibleColumnsState(columnsState: GridColumnsState): GridColumnsState {
  const modelWithVisibleColumns = Object.entries(
    columnsState.columnVisibilityModel ?? {}
  ).reduce<GridColumnVisibilityModel>((acc, [colField, isVisible]) => {
    if (!isVisible) return acc;
    acc[colField] = isVisible;
    return acc;
  }, {});
  const visibleColumnIds = Object.keys(modelWithVisibleColumns);

  return {
    columnVisibilityModel: modelWithVisibleColumns,
    dimensions: pick(visibleColumnIds, columnsState.dimensions ?? {}),
    orderedFields:
      columnsState.orderedFields?.filter(field => visibleColumnIds.includes(field)) ?? [],
  };
}

// Gets *visible* state (e.g., for use in saving Report).
export function getVisibleReportState(state: ReportGridState): ReportGridState {
  const { columns, filter, pinnedColumns, rowGrouping, sorting } = state;

  return cleanDeep(
    {
      columns: getVisibleColumnsState(columns ?? {}),
      filter: getFilterState(filter),
      pinnedColumns,
      rowGrouping,
      sorting,
    },
    {
      NaNValues: false,
      emptyArrays: false,
      emptyObjects: false,
      emptyStrings: false,
      nullValues: false,
      /**
       * Weirdly, in columns.dimensions, the `flex` value can be undefined causing downstream isEqual comparisons to fail.
       * Clean out undefined values to fix downstream deep-equal comparisons.
       */
      undefinedValues: true,
    }
  );
}

export function getFilterState(filter: GridInitialState['filter']): GridInitialState['filter'] {
  if (!filter?.filterModel) return filter;

  const {
    filterModel: { items, logicOperator },
  } = filter;
  // Omit quick filter values from state that is saved.
  return { filterModel: { items, logicOperator } };
}

/**
 * Reads validated `serverState` from the report. Returns `malformed: true`
 * when `report.state` fails the `getIsGridState` guard, so callers can log
 * observability events before falling back to the empty default.
 */
export function getServerStateFromReport(report: GridReport): {
  malformed: boolean;
  serverState: Partial<GridInitialState>;
} {
  if (getIsGridState(report.state)) return { malformed: false, serverState: report.state };
  return { malformed: true, serverState: {} };
}

/**
 * Gets state to save during an auto-save: layout (columns, pinned) from the
 * grid, but filter, sorting, and row grouping from the server — so layout-only
 * auto-saves never flush pending manual edits. `serverState` must be passed
 * by the caller (see `getServerStateFromReport`) so callers own the
 * malformed-state observability path.
 */
export function getAutoSaveLayoutState(
  report: GridReport,
  gridState: GridInitialState,
  serverState: Partial<GridInitialState>
): GridInitialState {
  const visibleGridState = getVisibleReportState(gridState);
  const removedColumns = new Set(getColumnsRemovedFromReport(report));

  const layoutColumns = removedColumns.size
    ? {
        ...visibleGridState.columns,
        columnVisibilityModel: omit(
          [...removedColumns],
          visibleGridState.columns?.columnVisibilityModel
        ),
        dimensions: omit([...removedColumns], visibleGridState.columns?.dimensions),
        orderedFields:
          visibleGridState.columns?.orderedFields?.filter(field => !removedColumns.has(field)) ??
          [],
      }
    : visibleGridState.columns;

  return cleanDeep(
    {
      columns: layoutColumns,
      filter: getFilterState(serverState.filter),
      pinnedColumns: visibleGridState.pinnedColumns,
      rowGrouping: serverState.rowGrouping,
      sorting: serverState.sorting,
    },
    {
      NaNValues: false,
      emptyArrays: false,
      emptyObjects: false,
      emptyStrings: false,
      nullValues: false,
      undefinedValues: true,
    }
  );
}

/**
 * Returns true if filter, sorting, or row grouping in the grid differ from
 * those saved on the server. Compares the *load-bearing* fields directly
 * (`items`, `sortModel`, `model`) so wrapper-object key drift (e.g. an
 * undefined `logicOperator` slot) does not produce false positives.
 */
export function hasManualUnsavedChanges(
  gridState: GridInitialState,
  serverState: Partial<GridInitialState>
): boolean {
  const currentVisible = getVisibleReportState(gridState);
  const currentFilterItems = currentVisible.filter?.filterModel?.items ?? [];
  const serverFilterItems = getFilterState(serverState.filter)?.filterModel?.items ?? [];
  if (!isEqual(currentFilterItems, serverFilterItems)) return true;

  const currentSort = currentVisible.sorting?.sortModel ?? [];
  const serverSort = serverState.sorting?.sortModel ?? [];
  if (!isEqual(currentSort, serverSort)) return true;

  const currentGrouping = currentVisible.rowGrouping?.model ?? [];
  const serverGrouping = serverState.rowGrouping?.model ?? [];
  return !isEqual(currentGrouping, serverGrouping);
}

// Gets state to save when a column has been deleted from a Report (e.g., due to tag deletion or merging).
export function getReportStateOnColumnDeletion(
  report: GridReport,
  gridState: ReportGridState
): ReportGridState {
  const visibleGridState = getVisibleReportState(gridState);
  const removedColumns = new Set(getColumnsRemovedFromReport(report));
  if (!removedColumns.size) return visibleGridState;

  return {
    ...visibleGridState,
    columns: {
      ...visibleGridState.columns,
      columnVisibilityModel: omit(
        [...removedColumns],
        visibleGridState.columns?.columnVisibilityModel
      ),
      dimensions: omit([...removedColumns], visibleGridState.columns?.dimensions),
      orderedFields:
        visibleGridState.columns?.orderedFields?.filter(field => !removedColumns.has(field)) ?? [],
    },
    filter: {
      ...visibleGridState.filter,
      filterModel: {
        ...visibleGridState.filter?.filterModel,
        items:
          visibleGridState.filter?.filterModel?.items.filter(
            item => !removedColumns.has(item.field)
          ) ?? [],
      },
    },
    // Impossible for columns to be removed from row grouping, so just return the existing state.
    rowGrouping: visibleGridState.rowGrouping,
    sorting: {
      ...visibleGridState.sorting,
      sortModel: visibleGridState.sorting?.sortModel?.filter(
        item => !removedColumns.has(item.field)
      ),
    },
  };
}

// Get column definitions for grid from colOptions and persisted Report.state.
export function getColDefsFromReport(report: GridReport): ReportGridColDef[] {
  const state = getGridStateToRestore(report);

  return report.columnOptions
    .map(c => {
      const dimensions = state.columns?.dimensions?.[getColOptionIdentifier(c)];
      // Spreading ...dimensions prevents jump in UI due to column width change when we apiRef.restoreState().
      return createReportGridColDef(c, { ...dimensions });
    })
    .sort((a, b) => {
      if (!state.columns?.orderedFields) return 0;

      const indexA = state.columns.orderedFields.indexOf(a.field);
      const indexB = state.columns.orderedFields.indexOf(b.field);
      return indexA - indexB;
    });
}

// Function to get the state to restore to a given grid (e.g., Report on Documents).
export function getGridStateToRestore(report: GridReport): GridInitialState {
  if (!getIsGridState(report.state) || isEmpty(report.state))
    return getDefaultStateToRestore(report.columnOptions);

  return {
    ...report.state,
    columns: getColumnStateToRestore(report.state.columns ?? {}, report.columnOptions),
  };
}

// Function to get the column state to restore to a given grid (e.g., Report on Documents).
export function getColumnStateToRestore(
  columnsState: GridColumnsInitialState,
  columnOptions: GridReportColOption[]
): GridColumnsInitialState {
  const orderedFieldsFromState =
    columnsState.orderedFields?.filter(
      field => !HIDDEN_REPORT_RUNTIME_COLUMN_FIELDS.includes(field)
    ) ?? [];
  const nonVisibleOrderedFields = columnOptions.flatMap(colOption => {
    const identifier = getColOptionIdentifier(colOption);
    // If field/column is already visible, don't include it...
    if (orderedFieldsFromState.includes(identifier)) return [];
    return [identifier];
  });
  const orderedFields = [...orderedFieldsFromState, ...nonVisibleOrderedFields];
  const columnVisibilityModel = {
    ...columnsState.columnVisibilityModel,
    ...nonVisibleOrderedFields.reduce<GridColumnsInitialState['columnVisibilityModel']>(
      (acc, nonVisibleField) => {
        if (acc) acc[nonVisibleField] = false;
        return acc;
      },
      {}
    ),
    ...HIDDEN_REPORT_RUNTIME_COLUMN_FIELDS.reduce<GridColumnsInitialState['columnVisibilityModel']>(
      (acc, field) => {
        if (acc) acc[field] = false;
        return acc;
      },
      {}
    ),
  };

  return {
    ...columnsState,
    columnVisibilityModel,
    orderedFields,
  };
}

// Function to get the default state to restore to a given grid (e.g., Report on Documents).
export function getDefaultStateToRestore(columnOptions: GridReportColOption[]): GridInitialState {
  const defaultOrderedFields: string[] = [
    GRID_CHECKBOX_SELECTION_FIELD,
    Apollo.ReportColumnFileField.Name,
    Apollo.ReportColumnFileField.Type,
    Apollo.ReportColumnFileField.Status,
    Apollo.ReportColumnFileField.Labels,
    Apollo.ReportColumnFileField.Owner,
    Apollo.ReportColumnFileField.CreatedAt,
  ];
  const nonVisibleOrderedFields = columnOptions.flatMap(colOption => {
    const identifier = getColOptionIdentifier(colOption);
    if (defaultOrderedFields.includes(identifier)) return [];
    return [identifier];
  });
  const orderedFields = [...defaultOrderedFields, ...nonVisibleOrderedFields];
  const columnVisibilityModel = {
    ...defaultOrderedFields.reduce<GridColumnsInitialState['columnVisibilityModel']>(
      (acc, nonVisibleField) => {
        if (acc) acc[nonVisibleField] = true;
        return acc;
      },
      {}
    ),
    ...nonVisibleOrderedFields.reduce<GridColumnsInitialState['columnVisibilityModel']>(
      (acc, nonVisibleField) => {
        if (acc) acc[nonVisibleField] = false;
        return acc;
      },
      {}
    ),
  };
  const dimensions = columnOptions.reduce<GridColumnsInitialState['dimensions']>(
    (acc, colOption) => {
      if (acc) acc[getColOptionIdentifier(colOption)] = getColWidthProperties(colOption);
      return acc;
    },
    {}
  );

  return {
    columns: {
      columnVisibilityModel,
      dimensions,
      orderedFields,
    },
  };
}

/**
 * Function that determines whether a column was removed from the DB.  For instance, a variable can be deleted
 * or merged with another tag, but a particular report might use that tag for a column in the report.
 */
export function getColumnsRemovedFromReport(report: GridReport): string[] {
  const state = getGridStateToRestore(report);
  const orderedFields = state.columns?.orderedFields ?? [];
  const savedFieldsWithoutCheck = orderedFields.filter(
    field => field !== GRID_CHECKBOX_SELECTION_FIELD
  );
  const columnOptionFields = new Set(
    report.columnOptions.map(colOption => getColOptionIdentifier(colOption))
  );

  // If Report.columnOptions doesn't contain the relevant field, that means it was deleted from the DB...
  return savedFieldsWithoutCheck.filter(field => !columnOptionFields.has(field));
}

// Type guard for checking that cell value is a partial of Apollo.ContractType.
export function getIsTypeCellValue(
  value: unknown
): value is Partial<Pick<Apollo.ContractType, '__typename' | 'value'>> {
  // Watch out!  typeof null === 'object'.
  return value !== null && typeof value === 'object';
}

/**
 * Minimal structural shape of the MUI grid API consumed here. Using a
 * structural type instead of the full `GridApi` lets tests pass a small
 * stub without `as unknown as GridApi` casts, and keeps the hook honest
 * about what it actually depends on.
 */
export type GridApiLike = Pick<GridApi, 'exportState' | 'restoreState' | 'subscribeEvent'>;

export type GridApiLikeRef = { readonly current: GridApiLike | null };

type SaveReportFn = (
  reportId: UUID,
  state: Pick<GridInitialState, 'columns' | 'filter' | 'rowGrouping' | 'sorting'>
) => Promise<{ data?: Apollo.UpdateReportMutation | null } | null | undefined>;

export type PerformReportSaveResult =
  | { error: unknown; kind: 'error'; silent: boolean }
  | { kind: 'no-data'; silent: boolean }
  | { kind: 'no-op' }
  | { hasManualDirty: boolean; kind: 'success'; silent: true; updatedReport: GridReport }
  | { kind: 'success'; silent: false; updatedReport: GridReport }
  | { kind: 'superseded' };

/**
 * Pure-ish orchestrator for the report save. Never throws — all failure
 * modes are encoded in the returned result so callers can render the
 * appropriate notification and observability events without re-implementing
 * the orchestration.
 *
 * Race-safety: each call captures a monotonic sequence id from
 * `saveSeqRef` before awaiting the mutation. If a Save/Discard handler
 * bumps `saveSeqRef.current` while a silent auto-save is in flight, the
 * stale save resolves with `{ kind: 'superseded' }` and applies no side
 * effects (no `restoreState`, no dirty-flag change).
 */
export async function performReportSave(args: {
  gridApi: GridApiLike | null;
  report: GridReport | null | undefined;
  reportId: UUID;
  saveReport: SaveReportFn;
  saveSeqRef: { current: number };
  silent: boolean;
  withSilentRestore: (restore: () => void) => void;
}): Promise<PerformReportSaveResult> {
  const { gridApi, report, reportId, saveReport, saveSeqRef, silent, withSilentRestore } = args;
  if (!gridApi || !report) return { kind: 'no-op' };

  const mySeq = ++saveSeqRef.current;
  const gridState = gridApi.exportState();
  const { serverState: serverStateFromReport } = getServerStateFromReport(report);
  const stateToSave = silent
    ? getAutoSaveLayoutState(report, gridState, serverStateFromReport)
    : getReportStateOnColumnDeletion(report, gridState);

  let saveData: { data?: Apollo.UpdateReportMutation | null } | null | undefined;
  try {
    saveData = await saveReport(reportId, stateToSave);
  } catch (error) {
    if (mySeq !== saveSeqRef.current) return { kind: 'superseded' };
    return { error, kind: 'error', silent };
  }

  if (mySeq !== saveSeqRef.current) return { kind: 'superseded' };

  const savedReport = saveData?.data?.updateReport?.report;
  if (!savedReport) return { kind: 'no-data', silent };

  const columnOptions = report.columnOptions;
  const updatedReport: GridReport = { ...savedReport, columnOptions };

  if (silent) {
    /**
     * Restore server layout but keep the grid's *post-await* filter/sort/
     * grouping so any manual edits the user typed during the debounce +
     * request window survive the auto-save.
     */
    const postSaveGridState = gridApi.exportState();
    withSilentRestore(() => {
      const restoredState = getGridStateToRestore(updatedReport);
      gridApi.restoreState({
        ...restoredState,
        filter: postSaveGridState.filter,
        rowGrouping: postSaveGridState.rowGrouping,
        sorting: postSaveGridState.sorting,
      });
    });
    const { serverState: savedServerState } = getServerStateFromReport(updatedReport);
    return {
      hasManualDirty: hasManualUnsavedChanges(postSaveGridState, savedServerState),
      kind: 'success',
      silent: true,
      updatedReport,
    };
  }

  withSilentRestore(() => {
    gridApi.restoreState(getGridStateToRestore(updatedReport));
  });
  return { kind: 'success', silent: false, updatedReport };
}

/**
 * Tracks user edits to the report grid and dispatches them into two
 * buckets:
 *  - **Auto-save** (column visibility, order, width, pin) — debounced and
 *    flushed via `onAutoSave`. The hook owns the debounce so callers don't
 *    need to wire one up; they can drop pending work via the returned
 *    `cancelPendingAutoSave`.
 *  - **Manual** (filter, sort, row grouping) — exploratory edits, surface
 *    a Save Changes button via `onStateChange`.
 */
export function useTrackReportStateChanges(args: {
  apiRef: GridApiLikeRef;
  /**
   * Called (debounced, trailing) for grid events that should auto-save
   * (column visibility, order, width, pin). Layout-only "commit now"
   * edits that do not flush pending filter/sort/grouping changes.
   */
  onAutoSave: () => Promise<void> | void;
  /**
   * Called for grid events that should flip the manual "unsaved changes"
   * dirty flag (filter, sort, row grouping). Exploratory edits that stay
   * behind the Save Changes button.
   */
  onStateChange: () => void;
  reportState: GridInitialState;
  /**
   * Debounce window in ms before `onAutoSave` fires after a trailing
   * auto-save-bucket event. Defaults to 500ms; tests can shrink this.
   */
  autoSaveDebounceMs?: number;
}): {
  cancelPendingAutoSave: () => void;
  withSilentRestore: (restore: () => void) => void;
} {
  const { apiRef, autoSaveDebounceMs = 500, onAutoSave, onStateChange, reportState } = args;

  /**
   * Callers typically pass inline arrow functions for `onAutoSave` /
   * `onStateChange`, which would otherwise change identity every render. If
   * either is listed in the subscribe effect's dep array, that effect would
   * tear down on every parent render — cancelling the trailing auto-save
   * timer mid-window (e.g. when an Apollo subscription update arrives
   * during the 500ms debounce). Stash the latest callbacks in refs and
   * read through them from the effect + debounced wrapper instead.
   */
  const onAutoSaveRef = useRef(onAutoSave);
  const onStateChangeRef = useRef(onStateChange);
  useEffect(() => {
    onAutoSaveRef.current = onAutoSave;
    onStateChangeRef.current = onStateChange;
  });

  const debouncedAutoSave = useDebouncedCallback(
    () => {
      const result = onAutoSaveRef.current();
      if (result instanceof Promise) {
        result.catch(err =>
          Sentry.captureException(err, {
            tags: { feature: 'report-auto-save', site: 'hook-debounce' },
          })
        );
      }
    },
    autoSaveDebounceMs,
    { trailing: true }
  );
  /**
   * The grid fires column/filter events synchronously while hydrating from
   * `reportState`, and `reportState` from the server can differ from the
   * grid's exported state even when nothing has changed (serialization drift).
   * Capture the grid's settled state as the baseline and compare against
   * that so only real user edits trigger the callbacks.
   */
  const isPausedRef = useRef(false);
  const baselineRef = useRef<GridInitialState | null>(null);

  /**
   * Run a programmatic `restoreState` without re-flagging unsaved changes.
   * MUI dispatches the column/filter events asynchronously, so we pause
   * tracking around the call and defer re-baselining + resume to the next
   * tick once those events have flushed. Without this, post-restore events
   * compare the grid against a stale baseline (serialization drift between
   * the raw report state and the grid's normalized settled state) and
   * immediately re-flag unsaved changes.
   */
  const withSilentRestore = useCallback(
    (restore: () => void) => {
      isPausedRef.current = true;
      restore();
      setTimeout(() => {
        if (apiRef.current) {
          baselineRef.current = getVisibleReportState(apiRef.current.exportState());
        }
        isPausedRef.current = false;
      }, 0);
    },
    [apiRef]
  );

  useEffect(() => {
    if (apiRef.current) {
      baselineRef.current = getVisibleReportState(apiRef.current.exportState());
    }

    const hasRealDiff = () => {
      if (isPausedRef.current) return false;
      if (!apiRef.current || !baselineRef.current) return false;
      const visibleGridState = getVisibleReportState(apiRef.current.exportState());
      return !isEqual(visibleGridState, baselineRef.current);
    };

    const handleAutoSaveBucket = () => {
      if (hasRealDiff()) debouncedAutoSave();
    };
    const handleManualBucket = () => {
      if (hasRealDiff()) onStateChangeRef.current();
    };

    /**
     * Subscribe to specific state-changing events instead of generic stateChange b/c
     * state change fires A LOT.
     * Auto-save bucket: visibility (add/hide), order, width, pin.
     * Manual bucket: filter, sort, row grouping — exploratory edits, stay behind the Save button.
     */
    const unsubscribeWidth = apiRef.current?.subscribeEvent(
      'columnWidthChange',
      handleAutoSaveBucket
    );
    const unsubscribeOrder = apiRef.current?.subscribeEvent(
      'columnOrderChange',
      handleAutoSaveBucket
    );
    const unsubscribeVisibility = apiRef.current?.subscribeEvent(
      'columnVisibilityModelChange',
      handleAutoSaveBucket
    );
    const unsubscribePinned = apiRef.current?.subscribeEvent(
      'pinnedColumnsChange',
      handleAutoSaveBucket
    );
    const unsubscribeSort = apiRef.current?.subscribeEvent('sortModelChange', handleManualBucket);
    const unsubscribeFilter = apiRef.current?.subscribeEvent(
      'filterModelChange',
      handleManualBucket
    );
    const unsubscribeRowGrouping = apiRef.current?.subscribeEvent(
      'rowGroupingModelChange',
      handleManualBucket
    );

    return () => {
      unsubscribeWidth?.();
      unsubscribeOrder?.();
      unsubscribeVisibility?.();
      unsubscribePinned?.();
      unsubscribeFilter?.();
      unsubscribeSort?.();
      unsubscribeRowGrouping?.();
      /**
       * `use-debounce` v9 doesn't auto-cancel on unmount, and this effect
       * re-runs when `reportState` / `onStateChange` identity changes — drop
       * any armed trailing timer so it can't fire against a stale closure.
       */
      debouncedAutoSave.cancel();
    };
  }, [apiRef, debouncedAutoSave, reportState]);

  return { cancelPendingAutoSave: debouncedAutoSave.cancel, withSilentRestore };
}

function getBuiltInReportColumnHeader(field: Apollo.ReportColumnFileField): string {
  switch (field) {
    case ReportColumnFileField.Id:
      return 'ID';
    case ReportColumnFileField.Name:
      return 'Name';
    case ReportColumnFileField.Status:
      return 'Status';
    case ReportColumnFileField.Type:
      return 'Contract Type';
    case ReportColumnFileField.FileType:
      return 'File Type';
    case ReportColumnFileField.Owner:
      return 'Owner';
    case ReportColumnFileField.Parties:
      return 'Parties';
    case ReportColumnFileField.RequesterFavorited:
      return 'Favorited';
    case ReportColumnFileField.Labels:
      return 'Labels';
    case ReportColumnFileField.CreatedAt:
      return 'Created At';
    case ReportColumnFileField.UpdatedAt:
      return 'Updated At';
    case ReportColumnFileField.DeletedAt:
      return 'Deleted At';
    case ReportColumnFileField.SignedAt:
      return 'Signed At';
    case ReportColumnFileField.LastAccessedByRequesterAt:
      return 'Last Accessed';
    default:
      return field;
  }
}
